import { Button, Card, CardActions, CardContent, CardHeader, Container, Box, Typography, Paper, Grid, Alert } from "@mui/material"
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout"
import { useNavigate, useParams } from "react-router"
import { QueryClient, useMutation, useQuery } from "@tanstack/react-query"
import { deleteCommunity, getCommunity } from "../../controllers/community/community"
import { useContext, useEffect } from "react"
import { UserContext } from "../../context/UserContext"
import { Warning, ArrowBack, Delete, Group } from "@mui/icons-material"

export const DeleteCommunity = () => {
    const { id } = useParams()
    let navigate = useNavigate()    
    const { userDetails } = useContext(UserContext)
    let token = userDetails?.token
    const { data:community, refetch } = useQuery({ queryKey: ['getSingleCommunity'], queryFn: () => getCommunity(token, id) })

    useEffect(() => {
        refetch()
    })
    
    const queryClient = new QueryClient()
    const mutation = useMutation({
        mutationFn: deleteCommunity,
        onSuccess: () => {
        // Invalidate and refetch
        queryClient.invalidateQueries({ queryKey: ['deleteCommunity'] })
        navigate('/community/list')
        },
    })

    const deleteComm = () => {
        mutation.mutate({
          id,
          token
        })
    }
    return (
        <MainLoggedInLayout>
            {community?.community?.leader && community?.community?.leader?.includes(userDetails?.id) ? (
                <Container maxWidth="md" sx={{ py: 4 }}>
                    <Card sx={{ 
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                        border: '2px solid',
                        borderColor: 'error.light'
                    }}>
                        <CardHeader 
                            avatar={
                                <Warning sx={{ color: 'error.main', fontSize: 40 }} />
                            }
                            title={
                                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                    Delete Community
                                </Typography>
                            }
                            subheader={
                                <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
                                    This action cannot be undone
                                </Typography>
                            }
                            sx={{ pb: 1 }}
                        />
                        
                        <CardContent sx={{ p: 4 }}>
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    Warning: This is a permanent action that affects all community members
                                </Typography>
                            </Alert>

                            <Paper sx={{ p: 3, backgroundColor: '#fff3e0', border: '1px solid #ffcc02' }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                    You are about to delete:
                                </Typography>
                                
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                        Community: {community?.community?.title || 'Community'}
                                    </Typography>
                                    {community?.community?.category && (
                                        <Typography variant="body2" color="text.secondary">
                                            Category: {community.community.category}
                                        </Typography>
                                    )}
                                    {community?.community?.privacy !== undefined && (
                                        <Typography variant="body2" color="text.secondary">
                                            Privacy: {community.community.privacy ? 'Private' : 'Public'}
                                        </Typography>
                                    )}
                                    {community?.community?.member_count && (
                                        <Typography variant="body2" color="text.secondary">
                                            Members: {community.community.member_count} people
                                        </Typography>
                                    )}
                                </Box>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                    This will permanently delete:
                                </Typography>
                                
                                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                                    <Typography component="li" variant="body2" color="text.secondary">
                                        The community and all its content
                                    </Typography>
                                    <Typography component="li" variant="body2" color="text.secondary">
                                        All member posts and discussions
                                    </Typography>
                                    <Typography component="li" variant="body2" color="text.secondary">
                                        Community photos and media files
                                    </Typography>
                                    <Typography component="li" variant="body2" color="text.secondary">
                                        Member lists and relationships
                                    </Typography>
                                    <Typography component="li" variant="body2" color="text.secondary">
                                        Community settings and customizations
                                    </Typography>
                                    <Typography component="li" variant="body2" color="text.secondary">
                                        All community analytics and history
                                    </Typography>
                                </Box>
                            </Paper>

                            <Alert severity="warning" sx={{ mt: 3 }}>
                                <Typography variant="body2">
                                    <strong>Important:</strong> All community members will lose access immediately. 
                                    Consider notifying members before deletion or transferring leadership if you want the community to continue.
                                </Typography>
                            </Alert>
                        </CardContent>
                        
                        <CardActions sx={{ p: 3, backgroundColor: '#fafafa' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Button 
                                        fullWidth 
                                        variant="outlined" 
                                        startIcon={<ArrowBack />}
                                        onClick={() => navigate("/community/show/"+community?.community?._id)}
                                        sx={{ 
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Cancel & Go Back
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Button 
                                        fullWidth 
                                        variant="contained" 
                                        color="error"
                                        startIcon={<Delete />}
                                        onClick={deleteComm}
                                        sx={{ 
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            '&:hover': {
                                                backgroundColor: 'error.dark'
                                            }
                                        }}
                                    >
                                        Yes, Delete Forever
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardActions>
                    </Card>
                </Container>
            ) : (
                <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                    <Paper sx={{ p: 4, borderRadius: 3 }}>
                        <Typography variant="h5" color="error" gutterBottom>
                            Access Denied
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            You don't have permission to delete this community. Only community leaders can delete their communities.
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={() => navigate("/community/list")}
                            sx={{ 
                                textTransform: 'none',
                                fontWeight: 'bold',
                                borderRadius: 2
                            }}
                        >
                            Browse Communities
                        </Button>
                    </Paper>
                </Container>
            )}
        </MainLoggedInLayout>
    )
}